import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from 'react';
import {
  workbooks,
  BUNDLE_PRICE,
  BUNDLE_PRICE_ID,
  VOLUME_TIERS,
  tierForCount,
  nextTierForCount,
  type Workbook,
} from '@/data/workbooks';

const STORAGE_KEY = 'mnl-workbook-cart';

interface CartTotals {
  /** Titles in the cart, in the order they were added */
  items: Workbook[];
  count: number;
  /** Sum of the individual prices, before any discount */
  subtotal: number;
  percentOff: number;
  /** Amount actually charged */
  total: number;
  saving: number;
  /** True when the cart is charged as the complete collection */
  isBundle: boolean;
  /** How many more titles until the next discount level, null at the top */
  nextTier: { needed: number; percentOff: number } | null;
}

interface CartValue extends CartTotals {
  has: (slug: string) => boolean;
  add: (slug: string) => void;
  remove: (slug: string) => void;
  toggle: (slug: string) => void;
  clear: () => void;
  /** Price lookup keys to send to checkout */
  priceIds: string[];
  isOpen: boolean;
  setOpen: (open: boolean) => void;
}

const CartContext = createContext<CartValue | null>(null);

function readStored(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (slug: unknown) =>
        typeof slug === 'string' && workbooks.some((w) => w.slug === slug),
    );
  } catch {
    return [];
  }
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [slugs, setSlugs] = useState<string[]>(readStored);
  const [isOpen, setOpen] = useState(false);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(slugs));
    } catch {
      /* storage blocked — the cart simply won't persist */
    }
  }, [slugs]);

  const add = useCallback((slug: string) => {
    setSlugs((prev) => (prev.includes(slug) ? prev : [...prev, slug]));
  }, []);

  const remove = useCallback((slug: string) => {
    setSlugs((prev) => prev.filter((s) => s !== slug));
  }, []);

  const toggle = useCallback((slug: string) => {
    setSlugs((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug],
    );
  }, []);

  const clear = useCallback(() => setSlugs([]), []);

  const value = useMemo<CartValue>(() => {
    const items = slugs
      .map((slug) => workbooks.find((w) => w.slug === slug))
      .filter((w): w is Workbook => Boolean(w));

    const count = items.length;
    const subtotal = items.reduce((sum, w) => sum + w.price, 0);
    const isBundle = count === workbooks.length;
    const percentOff = isBundle ? 0 : (tierForCount(count)?.percentOff ?? 0);
    const total = isBundle
      ? BUNDLE_PRICE
      : Math.round(subtotal * (1 - percentOff / 100));

    const next = isBundle ? null : nextTierForCount(count);

    return {
      items,
      count,
      subtotal,
      percentOff,
      total,
      saving: subtotal - total,
      isBundle,
      nextTier: next,
      priceIds: isBundle ? [BUNDLE_PRICE_ID] : items.map((w) => w.priceId),
      has: (slug: string) => slugs.includes(slug),
      add,
      remove,
      toggle,
      clear,
      isOpen,
      setOpen,
    };
  }, [slugs, isOpen, add, remove, toggle, clear]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartValue {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used inside CartProvider');
  return ctx;
}

export { VOLUME_TIERS };
